import { Component, h, Host, Prop, Element, Listen } from '@stencil/core';
import Swiper, {
  Navigation,
  Pagination,
  FreeMode,
  Virtual,
  EffectCoverflow,
  SwiperOptions,
  Autoplay,
} from 'swiper';

@Component({
  tag: 'me-slider',
  styleUrl: 'me-slider.scss',
  shadow: false,
})
export class MeSlider {

  @Element() host: HTMLMeSliderElement;

  @Prop() theme: string = 'primary';
  @Prop() lazyInitOffset = 500; // px

  @Prop() variant: 'normal' | 'collection' | 'teaser' | 'coverflow' | 'detail-images' = 'normal';
  // detail-images wurde eingeführt, weil es in der mobilen Artikeldetailansicht manchmal
  // zu einer unschönen Darstellung kam, wenn Bildunterschriften länger waren.

  @Prop() equalHeights?: boolean;
  @Prop() rewind?: boolean;
  @Prop() navigation?: boolean;
  @Prop() pagination?: boolean;

  @Prop() options?: SwiperOptions;

  protected readonly defaults: { [variant in typeof this.variant]: SwiperOptions } = {
    normal: {
      navigation: true,
      pagination: true,
    },
    collection: {
      freeMode: true,
      slidesPerView: 'auto',
      navigation: true,
      breakpoints: {
        0: {
          navigation: {
            enabled: false,
          }
        },
        768: {
          navigation: {
            enabled: true,
          }
        },
      }
    },
    teaser: {
      pagination: true,
      navigation: true,
      slidesPerView: 1,
      spaceBetween: 10,
      breakpoints: {
        0: {
          slidesPerView: 1,
          spaceBetween: 10,
          navigation: {
            enabled: false,
          },
          pagination: {
            enabled: true,
          }
        },
        578: {
          slidesPerView: 2,
          spaceBetween: 20,
          navigation: {
            enabled: false,
          },
          pagination: {
            enabled: true,
          }
        },
        768: {
          slidesPerView: 2,
          spaceBetween: 20,
          navigation: {
            enabled: true,
          },
          pagination: {
            enabled: true,
          }
        },
        992: {
          slidesPerView: 3,
          spaceBetween: 30,
          navigation: {
            enabled: true,
          },
          pagination: {
            enabled: true,
          }
        },
      },
    },
    coverflow: {
      breakpoints: {
        992: {
          slidesPerView: 1.25,
          spaceBetween: 100,
          centeredSlides: true,
          pagination: {
            enabled: false,
          },
          navigation: {
            enabled: true,
          },
        },
        576: {
          slidesPerView: 1.25,
          spaceBetween: 50,
          centeredSlides: true,
          pagination: {
            enabled: false,
          },
          navigation: {
            enabled: true,
          },
        },
        0: {
          slidesPerView: 1,
          spaceBetween: 10,
          pagination: {
            enabled: true,
          },
          navigation: {
            enabled: false,
          },
        }
      },
      effect: 'coverflow',
      coverflowEffect: {
        rotate: 0,
        slideShadows: false,
      },
      autoplay: {
        delay: 6_000,
        disableOnInteraction: false,
      },
      rewind: false,
      pagination: true,
      navigation: true,
    },
    'detail-images' : {
      navigation: true,
    }
  };

  protected slider?: HTMLElement;
  protected swiper?: Swiper;
  protected swiperWrapper?: HTMLDivElement;
  protected swiperPaginationEl?: HTMLDivElement;
  protected swiperPrevEl?: HTMLDivElement;
  protected swiperNextEl?: HTMLDivElement;

  private didWrapSlides = false;
  private didInit = false;

  protected get config() {
    const config = {...this.defaults[this.variant]};
    config.rewind = this.rewind ?? config.rewind ?? false;
    config.navigation = this.navigation ?? config.navigation ?? false;
    config.pagination = this.pagination ?? config.pagination ?? false;
    return {...config, ...this.options ?? {}};
  }

  private get shouldInit() {
    if (this.didInit) { return false; }

    const viewportHeight = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
    const sliderScrollY = scrollY + this.host.getBoundingClientRect().top;
    return scrollY + viewportHeight + this.lazyInitOffset >= sliderScrollY;
  }

  @Listen('scroll', { target: 'window' })
  handleScroll() {
    this.initSwiper();
  }

  connectedCallback() {
    this.initSwiper();
  }

  componentDidLoad() {
    this.wrapSlides();
    this.initSwiper();
  }

  componentDidUpdate() {
    this.destroySwiper();
    this.initSwiper();
  }

  disconnectedCallback() {
    this.destroySwiper();
  }

  render() {
    const classList = [
      'me-slider',
      `me-slider--${this.theme}`,
      this.variant ? `me-slider--${this.variant}` : null,
      this.equalHeights ? 'me-slider--equal-heights' : null,
      'swiper',
    ].filter(c => !!c).join(' ');

    return (
      <Host class={classList} ref={el => this.slider = el}>
        <div class="swiper-wrapper" ref={el => this.swiperWrapper = el}>
          <slot />
        </div>
        <div
          class="me-slider__pagination swiper-pagination"
          ref={el => this.swiperPaginationEl = el}
        />
        <div class="me-slider__button me-slider__button--prev" ref={el => this.swiperPrevEl = el}>
          <i class="icon fa-solid fa-xl fa-fw fa-chevron-left"></i>
        </div>
        <div class="me-slider__button me-slider__button--next" ref={el => this.swiperNextEl = el}>
          <i class="icon fa-solid fa-xl fa-fw fa-chevron-right"></i>
        </div>
      </Host>
    );
  }

  protected wrapSlides() {
    if (!this.swiperWrapper) { return; }

    this.swiperWrapper.replaceChildren(
      ...Array.from(this.swiperWrapper.children)
        .map(slide => {
          if (slide.classList.contains('swiper-slide')) { return slide; }

          const wrappedSlide = document.createElement('div');
          wrappedSlide.classList.add('swiper-slide');
          wrappedSlide.append(slide);

          return wrappedSlide;
        }),
    );

    this.didWrapSlides = true;
  }

  protected initSwiper() {
    if (!this.slider) { return; }
    if (!this.didWrapSlides) { return; }
    if (!this.shouldInit) { return; }

    const currentSlide = this.swiper?.realIndex ?? 0;

    this.destroySwiper();

    this.swiper = new Swiper(this.slider, {
      initialSlide: currentSlide,
      modules: [Navigation, Pagination, FreeMode, Virtual, EffectCoverflow, Autoplay],
      ...this.config,
      navigation: typeof this.config.navigation === 'object'
        ? this.config.navigation
        : {
          enabled: typeof this.config.navigation === 'boolean'
            ? this.config.navigation
            : false,
          prevEl: this.swiperPrevEl,
          nextEl: this.swiperNextEl,
          disabledClass: 'me-slider__button--disabled',
          navigationDisabledClass: 'me-slider--disabled-navigation',
        },
      pagination: typeof this.config.pagination === 'object'
        ? this.config.pagination
        : {
          enabled: typeof this.config.pagination === 'boolean'
            ? this.config.pagination
            : false,
          el: this.swiperPaginationEl,
          clickable: true,
          paginationDisabledClass: 'me-slider--disabled-pagination',
        },
    });

    this.didInit = true;
  }

  private destroySwiper() {
    this.swiper?.destroy(true, true);

    this.slider.classList.remove(
      'me-slider__button--disabled',
      'me-slider--disabled-navigation',
      'me-slider--disabled-pagination',
    );

    this.didInit = false;
  }
}
